import type { DayView, TripLeg } from "@poi-planner/shared";
import type { PlannerMode } from "../store/uiStore";
import {
  clampRollingWindowStart,
  rollingWindowLimits,
  startOfCalendarWeek,
} from "./rollingCalendar";
import { addDaysToDate } from "./zonedTime";

/**
 * Keeps the Planner's focus date within the trip. With nothing chosen yet it
 * falls back to `defaultDate` (today, while the trip is under way), then to
 * the first day.
 */
export function clampPlannerFocusDate(
  dayViews: DayView[],
  requestedDate: string | null,
  defaultDate: string | null = null,
): string | null {
  const firstDate = dayViews[0]?.date;
  const lastDate = dayViews[dayViews.length - 1]?.date;
  if (!firstDate || !lastDate) return null;
  requestedDate ??= defaultDate;
  if (!requestedDate || requestedDate < firstDate) return firstDate;
  if (requestedDate > lastDate) return lastDate;
  return requestedDate;
}

export function resolveActiveLeg(
  legs: TripLeg[],
  activeLegId: string | null,
  focusDate: string | null,
): TripLeg | null {
  const active = legs.find((leg) => leg.id === activeLegId);
  if (active) return active;
  return (
    legs.find(
      (leg) =>
        focusDate !== null &&
        leg.startDate <= focusDate &&
        focusDate <= leg.endDate,
    ) ??
    legs[0] ??
    null
  );
}

export function resolvePlannerCalendarStart(
  dayViews: DayView[],
  requestedStart: string | null,
  focusDate: string | null,
): string | null {
  const firstDate = dayViews[0]?.date;
  const lastDate = dayViews.at(-1)?.date;
  if (!firstDate || !lastDate) return null;
  const limits = rollingWindowLimits(firstDate, lastDate);
  return clampRollingWindowStart(
    requestedStart ?? startOfCalendarWeek(focusDate ?? firstDate),
    limits,
  );
}

export function selectPlannerDayViews(
  mode: PlannerMode,
  dayViews: DayView[],
  focusDate: string | null,
  activeLeg: TripLeg | null,
  calendarStart: string | null = null,
): DayView[] {
  if (mode === "day") {
    const date = clampPlannerFocusDate(dayViews, focusDate);
    return date ? dayViews.filter((dayView) => dayView.date === date) : [];
  }
  if (mode === "leg") {
    if (!activeLeg) return [];
    return dayViews.filter(
      (dayView) =>
        activeLeg.startDate <= dayView.date &&
        dayView.date <= activeLeg.endDate,
    );
  }
  if (mode === "calendar") {
    const start = resolvePlannerCalendarStart(
      dayViews,
      calendarStart,
      focusDate,
    );
    if (!start) return [];
    const end = addDaysToDate(start, 27);
    return dayViews.filter(
      (dayView) => start <= dayView.date && dayView.date <= end,
    );
  }
  return dayViews;
}
